import React, { useEffect, useState } from 'react';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import SectionMessage from '@atlaskit/section-message';
import { AsyncSelect, type OptionsType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import issueApi, { IssuePickerIssue, IssuePickerSection } from '../../../../api/issueApi';
import jiraExpression from '../../../../api/jiraExpression';

import { InputWrapper, ResultText, Text, Wrapper } from './TestingAreaStyled';

interface Props {
    jiraExpressionPatternToTest: string;
}

const TestingArea: React.FC<Props> = ({ jiraExpressionPatternToTest }) => {
    const [issueKey, setIssueKey] = useState('');
    const [testResult, setTestResult] = useState('');
    const [isJiraExpressionPatternValid, setIsJiraExpressionPatternValid] = useState(false);

    const handleTestValueChange = (newIssueKey: string) => {
        setIssueKey(newIssueKey);

        void testJqlExpression(jiraExpressionPatternToTest, newIssueKey);
    };

    const testJqlExpression = async (jiraexp: string, issueKey: string) => {
        try {
            const response = await jiraExpression.evaluateJiraExpression(jiraexp, issueKey);
            setIsJiraExpressionPatternValid(true);
            setTestResult(response);
        } catch (error: any) {
            setIsJiraExpressionPatternValid(false);
            setTestResult('An error occurred while evaluating the Jira expression.' + error.errorMessages[0]);
            return;
        }
    };

    const toIssueOption = (key: string, summary: string, id: string) => ({
        label: `${key} - ${summary}`,
        value: key,
        id,
    });

    const concatSectionsWithoutDuplicates = (sections: IssuePickerSection[]) => {
        const historySearchIssuesIds = sections[0].issues.map((issue: IssuePickerIssue) => issue.id);
        return [
            ...sections[0].issues,
            ...sections[1].issues.filter((issue: IssuePickerIssue) => !historySearchIssuesIds.includes(issue.id)),
        ];
    };

    const filterIssues = (sections: IssuePickerSection[]) => {
        const issues = sections.length > 1 ? concatSectionsWithoutDuplicates(sections) : sections[0].issues;

        return issues.map((issue: IssuePickerIssue) => toIssueOption(issue.key, issue.summaryText, issue.id));
    };

    const fetchIssuesAsync = async (inputValue: string): Promise<OptionsType> => {
        const response = await issueApi.getIssuePickerSuggestions(inputValue);

        return filterIssues(response.sections as IssuePickerSection[]);
    };

    useEffect(() => {
        const updateTestValue = async () => {
            if (issueKey) {
                await Promise.all([testJqlExpression(jiraExpressionPatternToTest, issueKey)]);
            }
        };
        void updateTestValue();
    }, [jiraExpressionPatternToTest]);

    return (
        <Wrapper>
            <SectionMessage
                title='Testing area'
                appearance='information'
                testId='testing-area-section-message'
            >
                <Text>Enter an example value to check if it meets the Jira expression configured above.</Text>
                <InputWrapper>
                    <AsyncSelect
                        isClearable
                        defaultOptions
                        cacheOptions
                        loadOptions={fetchIssuesAsync}
                        placeholder='Select issues'
                        onChange={selectedOption => handleTestValueChange(selectedOption?.value as string)}
                        className='issue-picker'
                    />
                    <div>
                        {jiraExpressionPatternToTest && issueKey?.length >= 1 ?
                            <>
                                {isJiraExpressionPatternValid ?
                                    <CheckCircleIcon
                                        primaryColor={token('color.text.accent.green')}
                                        label='Jira expression valid'
                                    />
                                :   <CrossCircleIcon
                                        primaryColor={token('color.text.accent.red')}
                                        label='Jira expression invalid'
                                    />
                                }
                            </>
                        :   null}
                    </div>
                </InputWrapper>

                {issueKey?.length >= 1 ?
                    <>
                        <ResultText>Test Result</ResultText>
                        <TextArea
                            value={testResult}
                            isReadOnly
                            isDisabled
                            appearance='subtle'
                        />
                    </>
                :   null}
            </SectionMessage>
        </Wrapper>
    );
};

export default TestingArea;
